/**
 * Hook TanStack Query per la vista "Batch Edits", con live polling.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchBatch,
  fetchBatchRecords,
  fetchBatches,
  fetchHoldThreshold,
  forceRunBatch,
  type HoldThresholdSettings,
} from './api.js';
import {
  FINISHED_STATUSES,
  type BatchDetail,
  type BatchSummary,
  type ForceRunResponse,
  type RecordsPage,
  type RecordsQuery,
} from './types.js';

/** Intervallo di live polling (ms). */
export const POLL_INTERVAL_MS = 2000;

export const batchEditKeys = {
  list: () => ['batch-edits', 'list'] as const,
  detail: (id: string) => ['batch-edits', 'detail', id] as const,
  records: (id: string, q: RecordsQuery) => ['batch-edits', 'records', id, q] as const,
  holdThreshold: () => ['batch-edits', 'hold-threshold'] as const,
};

/** Elenco batch: sempre in polling (nuovi batch possono arrivare in ogni momento via API). */
export function useBatches(): UseQueryResult<BatchSummary[]> {
  return useQuery({
    queryKey: batchEditKeys.list(),
    queryFn: () => fetchBatches(),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

const isFinished = (d: BatchDetail | undefined) => !!d && FINISHED_STATUSES.includes(d.status);

/** Dettaglio batch: polling finché il run non è concluso. */
export function useBatch(batchId: string): UseQueryResult<BatchDetail> {
  return useQuery({
    queryKey: batchEditKeys.detail(batchId),
    queryFn: () => fetchBatch(batchId),
    refetchInterval: (q) => (isFinished(q.state.data) ? false : POLL_INTERVAL_MS),
    refetchIntervalInBackground: false,
  });
}

/** Record del batch: in polling finché `live` (batch non concluso). */
export function useBatchRecords(
  batchId: string,
  query: RecordsQuery,
  live: boolean,
): UseQueryResult<RecordsPage> {
  return useQuery({
    queryKey: batchEditKeys.records(batchId, query),
    queryFn: () => fetchBatchRecords(batchId, query),
    placeholderData: keepPreviousData,
    refetchInterval: live ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });
}

/** Soglia di hold in vigore (per il dialogo di conferma del force-run). */
export function useHoldThreshold(enabled = true): UseQueryResult<HoldThresholdSettings> {
  return useQuery({
    queryKey: batchEditKeys.holdThreshold(),
    queryFn: fetchHoldThreshold,
    enabled,
    staleTime: 30_000,
  });
}

/** Force-run di un batch on-hold: a esito positivo ricarica dettaglio, elenco e record. */
export function useForceRunBatch(
  batchId: string,
): UseMutationResult<ForceRunResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => forceRunBatch(batchId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['batch-edits'] }),
  });
}
